import { Segmented, Flex, Button, Space, Form, Select, Typography } from "antd";
import MasterComponent from "../../../components/Forms";
import { MasterConfigs } from "../../../interfaces/entity";
import DeveloperDetails from "./DeveloperDetails";
import { useMemo, useState } from "react";
import DrawerComponent from "../../../components/Forms/Drawer";
import ProfileCard from "../../../components/ContactInfo/ProfileCard";
import SmallProfileCard from "../../../components/ContactInfo/SmallProfileCard";
import { constants } from "../../../constants/common";
import Badge from "../../../components/Badge";
import moment from "moment";
import { formatPriceIndian } from "../../../constants/functions";
import { useDispatch, useSelector } from "react-redux";
const { EditOutlined } = require("@ant-design/icons");
const { Title } = Typography;

const BillableDevelopers = () => {
    const [openDrawer, setOpenDrawer] = useState(false);
    const [selectedDeveloper, setSelectedDeveloper] = useState(null);
    const [addNew, setAddNew] = useState(false);
    const [segment, setSegment] = useState('All');
    const [formSubmitTrigger, setFormSubmitTrigger] = useState(0);
    const [selectedID, setSelectedID] = useState(null);
    const dispatch = useDispatch();
    const { user } = useSelector((app: any) => app?.app || {});
    const isAdmin = user?.designation === 'Admin';

    const config: MasterConfigs = useMemo(() => ({
        api: () => "/bench-developers/billable/list",
        queryParams: {
            vendorPartner: segment
        },
        title: "Billable Developers",
        isActionTab: false,
        getColumns: () => [
            {
                title: "Developer", dataIndex: "developer", key: "developer", render: (text, record) => (
                    <div className="flex items-center gap-2 cursor-pointer" onClick={() => {
                        setAddNew(false);
                        setSelectedDeveloper(record);
                        setOpenDrawer(true);
                    }}>
                        <SmallProfileCard
                            name={constants.GET_FULL_NAME(record)}
                            subTitle={record?.jobTitle?.name || ''}
                            avatar={record.developer?.avatar}
                        />
                    </div>
                ),
                width: 200,
                fixed: 'left'
            },
            {
                title: 'Experience',
                width: 150,
                align: 'center', dataIndex: 'experience', key: 'experience', render: (text) => (text ? <span>{text} Years</span> : null)
            },
            {
                title: "Budget Range",
                width: 200,
                dataIndex: "budgetRange", key: "budgetRange", render: (text) => (text ? <span>{formatPriceIndian(text)}</span> : null)
            },
            {
                title: 'Vendor',
                width: 200,
                dataIndex: 'vendorPartner', key: 'vendorPartner', render: (text, record) => (text?.vendorName ? <Space direction="vertical">
                    {text.vendorName}
                    {record?.vendorBudget ? <span className="text-xs text-gray-500">Budget: {formatPriceIndian(record.vendorBudget)}</span> : null}
                </Space> : <span>Own Profile</span>)
            },
            {
                title: 'Deal Name',
                width: 200,
                dataIndex: 'dealName',
                key: 'dealName',
                render: (text) => (text ? <span>{text}</span> : null)
            },
            {
                title: 'Deal Budget',
                width: 180,
                dataIndex: 'dealBudget',
                key: 'dealBudget',
                render: (text) => (text ? <span>{formatPriceIndian(text)}</span> : null)
            },
            {
                title: 'Company Name',
                width: 200,
                dataIndex: 'companyName',
                key: 'companyName',
                render: (text) => (text ? <span>{text}</span> : null)
            },
            {
                title: 'Project Start Date',
                width: 180,
                dataIndex: 'projectStartDate',
                key: 'projectStartDate',
                render: (text) => (text ? <span>{moment(text).format('DD MMM YYYY')}</span> : null)
            },
            {
                title: 'Project Close Date',
                width: 180,
                dataIndex: 'projectCloseDate',
                key: 'projectCloseDate',
                render: (text) => (text ? <span>{moment(text).format('DD MMM YYYY')}</span> : null)
            },
            {
                title: 'Developer Owner',
                dataIndex: 'developerOwner',
                key: 'developerOwner',
                width: 200,
                render: (text) => <SmallProfileCard
                    name={constants.GET_FULL_NAME(text)}
                    avatar={text?.avatar}
                />
            }
        ],
        actions: [],
        extraControles: [
            <Segmented
                key="segment"
                value={segment}
                options={['All', 'Own', 'Vendor']}
                onChange={(value) => {
                    setSegment(value);
                }}
            />
        ],
        modalTitle: (text) => text + ' Developer',
        getFormColumns: () => [
            {
                type: 'custom',
                span: 24,
                formItem: (form) => <Title level={5} className="font-bold border-b pb-2">Developer Information</Title>
            },
            {
                label: 'Developer Owner',
                name: 'developerOwner',
                type: 'select',
                isCombo: true,
                span: 12,
            },
            {
                label: 'Developer',
                name: 'developer',
                type: 'select',
                isCombo: true,
                selectProps: {
                    showSearch: true,
                    allowClear: true,
                },
                span: 12,
            },
            {
                label: 'Vendor',
                name: 'vendorPartner',
                type: 'select',
                isCombo: true,
                span: 12,
            },
            {
                label: 'First Name',
                name: 'firstName',
                type: 'text',
                inputProps: {
                    addonBefore: <Form.Item name="preName">
                        <Select style={{ width: 100 }} placeholder="Title">
                            {constants.PRENAME.map(ele => <Select.Option key={ele.value} value={ele.value}>{ele.name}</Select.Option>)}
                        </Select>
                    </Form.Item>
                },
                rules: [{ required: true, message: 'First Name is required' }],
                span: 12,
            },
            {
                label: 'Last Name',
                name: 'lastName',
                type: 'text',
                span: 12,
            },
            {
                label: 'Email',
                name: 'email',
                type: 'text',
                span: 12,
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                            return emailRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid email address'));
                        },
                    },
                ],
            },
            {
                label: 'Mobile',
                name: 'mobile',
                type: 'text',
                span: 12,
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const mobileRegex = /^[0-9]{10,15}$/;
                            return mobileRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid mobile number'));
                        },
                    },
                ],
            },
            {
                label: 'LinkedIn Profile',
                name: 'linkedIn',
                type: 'text',
                span: 12,
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value || (typeof value === 'string' && value.trim() === '')) {
                                return Promise.resolve();
                            }
                            const linkedInRegex = /^(https?:\/\/)?(www\.)?linkedin\.com\/in\/[\w-]+\/?$/i;
                            if (linkedInRegex.test(value.trim())) {
                                return Promise.resolve();
                            }
                            return Promise.reject(new Error('Enter a valid LinkedIn profile URL'));
                        },
                    },
                ],
                placeholder: 'https://www.linkedin.com/in/your-profile',
            },
            {
                label: 'Job Title',
                name: 'jobTitle',
                type: 'select',
                isCombo: true,
                span: 12,
                rules: [{ required: true, message: 'Job Title is required' }],
            },
            {
                label: 'Experience',
                name: 'experience',
                type: 'text',
                inputProps: {
                    addonAfter: 'Years',
                },
                rules: [
                    { required: true, message: 'Experience is required' },
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const experienceRegex = /^[0-9]{1,3}$/;
                            return experienceRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid experience'));
                        },
                    },
                ],
                span: 12,
            },
            {
                label: 'Developer Availability',
                name: 'developerAvailability',
                type: 'select',
                selectProps: {
                    showSearch: true,
                    optionFilterProp: 'children',
                    allowClear: true,
                },
                children: constants.DEVELOPER_AVAILABILITY,
                rules: [{ required: true, message: 'Developer Availability is required' }],
                span: 12,
            },
            {
                label: 'Priority',
                name: 'priority',
                type: 'select',
                selectProps: {
                    options: [
                        { label: 'High', value: 'High' },
                        { label: 'Medium', value: 'Medium' },
                        { label: 'Low', value: 'Low' },
                    ],
                },
                span: 12,
            },
            {
                type: 'custom',
                span: 24,
                formItem: (form) => <Title level={5} className="font-bold border-b pb-2">Notice Information</Title>
            },
            {
                label: 'Notice Period',
                name: 'noticePeriodDays',
                type: 'text',
                inputProps: {
                    addonAfter: 'Days',
                },
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const noticePeriodRegex = /^[0-9]{1,3}$/;
                            return noticePeriodRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid notice period'));
                        },
                    },
                ],
                span: 12,
            },
            {
                label: 'Last Working Date',
                name: 'lastWorkingDate',
                type: 'date',
                dateProps: {
                    format: 'DD/MM/YYYY',
                },
                span: 12,
            },
            {
                type: 'custom',
                span: 24,
                formItem: (form) => <Title level={5} className="font-bold border-b pb-2">Pricing Information</Title>
            },
            {
                label: 'Vendor Budget',
                name: 'vendorBudget',
                type: 'text',
                inputProps: {
                    prefix: 'INR ',
                },
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const rangeRegex = /^[0-9]+(\.[0-9]{1,2})?$/;
                            return rangeRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid vendor billing rate'));
                        },
                    },
                ],
                span: 12,
            },
            {
                label: 'Selling Price',
                name: 'budgetRange',
                type: 'text',
                inputProps: {
                    prefix: 'INR ',
                },
                rules: [
                    { required: true, message: 'Selling Price is required' },
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.reject(new Error('Selling Price is required'));
                            const budgetRange = /^\s*\d+(\.\d{1,2})?\s*-\s*\d+(\.\d{1,2})?\s*$/;
                            return budgetRange.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid budget range, e.g. 10000 - 20000'));
                        },
                    },
                ],
                span: 12,
            },
            {
                label: 'Vendor GST / LUT',
                name: 'vendorGST',
                type: 'text',
                inputProps: {
                    defaultValue: 18,
                    addonAfter: '%',
                },
                span: 12,
            },
            {
                label: 'TDS for Vendor',
                name: 'vendorTDS',
                type: 'text',
                inputProps: {
                    defaultValue: 10,
                    addonAfter: '%',
                },
                span: 12,
            },
            {
                type: 'custom',
                span: 24,
                formItem: (form) => <Title level={5} className="font-bold border-b pb-2">Description Information</Title>
            },
            {
                label: 'Description',
                name: 'description',
                type: 'textarea',
                span: 24,
                textAreaProps: {
                    autoSize: { minRows: 3, maxRows: 6 }
                }
            },
        ],
        combo: [
            // Developer Owner combo - always include, but for normal users, default to current user
            {
                api: '/users',
                name: 'developerOwner',
                idKey: '_id',
                render: (text) => <ProfileCard
                    size={30}
                    name={constants.GET_FULL_NAME(text)}
                    avatarUrl={text?.avatar ? process.env.FILE_URL + text?.avatar : null}
                    role={null}
                />
            },
            {
                api: '/users',
                name: 'developer',
                idKey: '_id',
                render: (text) => <ProfileCard
                    size={30}
                    name={constants.GET_FULL_NAME(text)}
                    avatarUrl={text?.avatar ? process.env.FILE_URL + text?.avatar : null}
                    role={null}
                />
            },
            {
                api: '/v1/vendor',
                name: 'vendorPartner',
                idKey: '_id',
                render: (text) => text?.vendorName || 'N/A'
            },
            {
                api: '/job-titles',
                name: 'jobTitle',
                idKey: '_id',
                render: (text) => text?.name || 'N/A'
            },
        ],
        setInitialForm: () => {
            // For normal users, set default developerOwner to current user when creating new developer
            if (!isAdmin && user?._id) {
                return { developerOwner: user._id };
            }
            return {};
        },
    }), [segment, isAdmin, user]);

    const onClose = () => {
        setOpenDrawer(false);
        setSelectedDeveloper(null);
        setAddNew(false);
        setSelectedID(null);
        dispatch({
            type: 'REFRESH_TABLE',
            refreshTable: true
        });
    }

    return (
        <div>
            <MasterComponent config={config} isModal={true} recordId={selectedID} />
            <DrawerComponent
                title={
                    !addNew ? <Flex justify="space-between" align="center" gap={2} style={{ width: '95%' }}>
                        <ProfileCard
                            size={40}
                            name={`${selectedDeveloper?.firstName || ''} ${selectedDeveloper?.lastName || ''}`}
                            avatarUrl={selectedDeveloper?.profilePhoto ? process.env.FILE_URL + selectedDeveloper?.profilePhoto : null}
                            role={selectedDeveloper?.title || 'Developer Details'}
                        />
                        {!selectedDeveloper?.converted && <div className="flex gap-2">
                            <Button disabled type="primary">Send Email</Button>
                            <Button icon={<EditOutlined />} onClick={() => {
                                setSelectedID(selectedDeveloper?._id);
                                setOpenDrawer(false);
                            }} />
                        </div>}
                    </Flex> : 'Add New Developer'
                }
                visible={openDrawer}
                handleCancel={() => setOpenDrawer(false)}
                isView={!addNew}
                loading={false}
                submitForm={() => {
                    setFormSubmitTrigger(prev => prev + 1);
                }}
                rest={{
                    width: '90%',
                    style: {
                        background: '#eef1f9',
                        padding: '0px !important',
                    },
                    bodyStyle: {
                        padding: 0,
                        overflowY: 'auto',
                        maxHeight: 'calc(100vh - 120px)',
                        display: 'flex',
                        flexDirection: 'column'
                    },
                    headerStyle: { background: '#FFFFFF', padding: '8px 10px' },
                }}
            >
                <DeveloperDetails developer={selectedDeveloper} addNew={addNew} onClose={() => onClose()} formSubmitTrigger={formSubmitTrigger} />
            </DrawerComponent>
        </div>
    );
};

export default BillableDevelopers;